import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, Pressable } from 'react-native-web';
import { colors, shadows, radius } from './theme';
import ScriptsPage from './pages/ScriptsPage';
import PixelsPage from './pages/PixelsPage';
import MappingsPage from './pages/MappingsPage';
import LogsPage from './pages/LogsPage';
import AdminPage from './pages/AdminPage';

const TABS = [
  { key: 'scripts', label: 'Scripts', icon: '📜' },
  { key: 'pixels', label: 'Pixels', icon: '🎯' },
  { key: 'mappings', label: 'Mappings', icon: '🔗' },
  { key: 'logs', label: 'Event Logs', icon: '📋' },
];

const ADMIN_TABS = [
  ...TABS,
  { key: 'admin', label: 'Admin', icon: '⚙️' },
];

/**
 * Decode JWT payload safely. Handles URL-safe base64.
 */
function decodeJwt(token) {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    // Handle URL-safe base64
    let payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    // Pad if needed
    while (payload.length % 4 !== 0) payload += '=';
    return JSON.parse(atob(payload));
  } catch (e) {
    console.error('Failed to decode JWT:', e);
    return null;
  }
}

export default function App() {
  const [activeTab, setActiveTab] = useState('scripts');
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('keycloak_token');
    if (token) {
      const decoded = decodeJwt(token);
      if (decoded) {
        setUser({
          preferred_username: decoded.preferred_username,
          email: decoded.email,
          name: decoded.name,
          sub: decoded.sub,
          realm_access: decoded.realm_access,
        });

        // Check if user has admin role
        const roles = decoded.realm_access?.roles || [];
        setIsAdmin(roles.includes('admin'));
        console.log('Logged in as:', decoded.preferred_username, 'roles:', roles);
      } else {
        // Invalid token, redirect to login
        localStorage.removeItem('keycloak_token');
        localStorage.removeItem('keycloak_refresh_token');
        window.location.href = '/login';
      }
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('keycloak_token');
    localStorage.removeItem('keycloak_refresh_token');
    window.location.href = '/login';
  };

  const renderPage = () => {
    switch (activeTab) {
      case 'scripts':
        return <ScriptsPage />;
      case 'pixels':
        return <PixelsPage />;
      case 'mappings':
        return <MappingsPage />;
      case 'logs':
        return <LogsPage />;
      case 'admin':
        return isAdmin ? <AdminPage /> : <ScriptsPage />;
      default:
        return null;
    }
  };

  const tabs = isAdmin ? ADMIN_TABS : TABS;

  return (
    <View style={styles.container}>
      {/* Sidebar */}
      <View style={styles.sidebar}>
        <View style={styles.logoContainer}>
          <Text style={styles.logoIcon}>◉</Text>
          <Text style={styles.logoText}>Pixel Mirror</Text>
        </View>

        <View style={styles.nav}>
          {tabs.map((tab) => (
            <Pressable
              key={tab.key}
              style={[styles.navItem, activeTab === tab.key && styles.navItemActive]}
              onPress={() => setActiveTab(tab.key)}
            >
              <Text style={styles.navIcon}>{tab.icon}</Text>
              <Text style={[styles.navLabel, activeTab === tab.key && styles.navLabelActive]}>
                {tab.label}
              </Text>
            </Pressable>
          ))}
        </View>

        {user && (
          <View style={styles.userSection}>
            <View style={styles.userAvatar}>
              <Text style={styles.userAvatarText}>
                {(user.preferred_username || user.email || '?')[0].toUpperCase()}
              </Text>
            </View>
            <Text style={styles.userName} numberOfLines={1}>
              {user.name || user.preferred_username || user.email}
            </Text>
            {isAdmin && (
              <Text style={styles.adminBadge}>Admin</Text>
            )}
            <Pressable style={styles.logoutBtn} onPress={handleLogout}>
              <Text style={styles.logoutText}>Logout</Text>
            </Pressable>
          </View>
        )}
      </View>

      {/* Main Content */}
      <View style={styles.main}>
        {renderPage()}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'row',
    minHeight: '100vh',
    backgroundColor: colors.bg,
  },
  sidebar: {
    width: 240,
    backgroundColor: colors.surface,
    borderRightWidth: 1,
    borderRightColor: colors.border,
    paddingVertical: 24,
    justifyContent: 'flex-start',
  },
  logoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    marginBottom: 32,
  },
  logoIcon: {
    fontSize: 24,
    color: colors.primary,
    marginRight: 10,
  },
  logoText: {
    fontSize: 18,
    fontWeight: '700',
    color: colors.white,
    letterSpacing: -0.5,
  },
  nav: {
    flex: 1,
  },
  navItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 20,
    marginHorizontal: 8,
    borderRadius: radius.sm,
    marginBottom: 2,
  },
  navItemActive: {
    backgroundColor: 'rgba(99, 102, 241, 0.15)',
  },
  navIcon: {
    fontSize: 16,
    marginRight: 12,
  },
  navLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: colors.textSecondary,
  },
  navLabelActive: {
    color: colors.primary,
    fontWeight: '600',
  },
  userSection: {
    borderTopWidth: 1,
    borderTopColor: colors.border,
    paddingTop: 16,
    paddingHorizontal: 20,
    alignItems: 'center',
  },
  userAvatar: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 8,
  },
  userAvatarText: {
    color: colors.white,
    fontWeight: '700',
    fontSize: 14,
  },
  userName: {
    fontSize: 12,
    color: colors.textSecondary,
    marginBottom: 4,
  },
  adminBadge: {
    fontSize: 10,
    color: colors.success,
    fontWeight: '700',
    backgroundColor: 'rgba(16,185,129,0.15)',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
    marginBottom: 8,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  logoutBtn: {
    paddingVertical: 4,
    paddingHorizontal: 12,
  },
  logoutText: {
    fontSize: 12,
    color: colors.textMuted,
  },
  main: {
    flex: 1,
    padding: 32,
    overflowY: 'auto',
  },
});
